import * as PIXI from 'pixi.js';
import { assertMaximum, assertMinimum, assertNumber } from '../validators';
import type { ParentMixinSettings } from './parentMixin';
import { ParentMixin } from './parentMixin';
import type { UpdateFlowMixinSettings } from './updateFlowMixin';
import { UpdateFlowMixin } from './updateFlowMixin';

type NumberArraySetttings = UpdateFlowMixinSettings &
    ParentMixinSettings & {
        /**
         * the minimum value to enforce
         */
        minimum?: number;
        /**
         * the maximum value to enforce
         */
        maximum?: number;
    };

export class NumberArray extends UpdateFlowMixin(
    ParentMixin<{ new (settings?: unknown): PIXI.utils.EventEmitter }>(PIXI.utils.EventEmitter),
) {
    override _classType = 'NumberArray';
    #array: Array<number> = [];
    #minimum: number | null = null;
    #maximum: number | null = null;

    constructor(settings: NumberArraySetttings = {}) {
        super(settings);

        if (settings.hasOwnProperty('minimum')) {
            assertNumber(settings.minimum as number);
            this.#minimum = settings.minimum!;
        }
        if (settings.hasOwnProperty('maximum')) {
            assertNumber(settings.maximum as number);
            this.#maximum = settings.maximum!;
        }
    }

    addNumberAtIndex(value: number, index: number) {
        assertNumber(value);
        if (this.#minimum !== null) {
            assertMinimum(value, this.#minimum);
        }
        if (this.#maximum !== null) {
            assertMaximum(value, this.#maximum);
        }
        this.#array.splice(index, 0, value);
        this.requiresUpdate = true;
    }

    moveIndex(currentIndex: number, newIndex: number) {
        const value = this.getValueAtIndex(currentIndex);
        this.#array.splice(currentIndex, 1);
        this.#array.splice(newIndex, 0, value);
        this.requiresUpdate = true;
    }

    removeIndex(index: number) {
        this.#array.splice(index, 1);
        this.requiresUpdate = true;
    }

    getValueAtIndex(index: number) {
        if (this.array.length <= index) {
            throw Error(`index ${index} does not exist`);
        }
        return this.#array[index]!;
    }

    setValueAtIndex(value: number, index: number) {
        const isAppending = index === this.array.length;
        if (!isAppending && this.getValueAtIndex(index) === value) {
            return;
        }
        assertNumber(value);
        this.#array[index] = value;
        this.requiresUpdate = true;
    }

    get array(): readonly number[] {
        return this.#array;
    }

    save(): Array<number> {
        return this.#array;
    }

    load(dataObj: Array<number>) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        dataObj.forEach((setting, index) => {
            this.setValueAtIndex(setting, index);
        });

        this.autoUpdate = initAutoUpdate;
    }
}
